import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, ButtonBase, Typography } from '@mui/material';

export type AnswerState = 'idle' | 'selected' | 'correct' | 'incorrect' | 'missed';

export interface AnswerOptionProps {
  label: string; // "A", "B", ...
  text: string;
  state: AnswerState;
  disabled?: boolean;
  multiple?: boolean;
  onSelect?: () => void;
}

/**
 * Large, thumb-friendly answer button with an unambiguous selected state.
 * Uses role=radio/checkbox semantics for screen readers.
 */
export function AnswerOption({ label, text, state, disabled, multiple, onSelect }: AnswerOptionProps) {
  const selected = state === 'selected' || state === 'correct' || state === 'incorrect';
  const tone = state === 'correct' || state === 'missed' ? 'success' : state === 'incorrect' ? 'error' : 'primary';
  const highlighted = state !== 'idle';

  return (
    <ButtonBase
      role={multiple ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onSelect}
      sx={(theme) => ({
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: 1.5,
        p: { xs: 1.75, sm: 2 },
        minHeight: 60,
        borderRadius: 3,
        border: '2px solid',
        borderColor: highlighted ? (theme.vars ?? theme).palette[tone].main : (theme.vars ?? theme).palette.divider,
        bgcolor: highlighted ? theme.alpha((theme.vars ?? theme).palette[tone].main, state === 'missed' ? 0.06 : 0.1) : (theme.vars ?? theme).palette.background.paper,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': disabled ? undefined : { borderColor: (theme.vars ?? theme).palette[tone].main },
        '&.Mui-disabled': { opacity: 1 },
      })}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          flexShrink: 0,
          width: 34,
          height: 34,
          borderRadius: multiple ? 1.5 : '50%',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 14,
          color: highlighted ? (theme.vars ?? theme).palette[tone].contrastText : (theme.vars ?? theme).palette.text.secondary,
          bgcolor: highlighted ? (theme.vars ?? theme).palette[tone].main : (theme.vars ?? theme).palette.action.hover,
        })}
      >
        {label}
      </Box>
      <Typography sx={{ flex: 1, fontSize: { xs: '1rem', sm: '1.05rem' }, lineHeight: 1.45 }}>{text}</Typography>
      {state === 'correct' && <CheckCircleIcon color="success" aria-label="Correct" />}
      {state === 'missed' && <CheckCircleIcon color="success" aria-label="Correct answer" />}
      {state === 'incorrect' && <CancelIcon color="error" aria-label="Incorrect" />}
    </ButtonBase>
  );
}

export const answerLetter = (index: number) => String.fromCharCode(65 + index);
